import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { usePartnerData } from "@/hooks/usePartnerData";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/utils";

const PortalCard = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div className={cn("rounded-lg border border-[#d0d7de] bg-white", className)}>{children}</div>
);

export function PartnerSettingsPanel() {
  const { profile, user, saveInstitutionName, updateProfile } = usePartnerData();
  const { refreshUserProfile, logout } = useAuth();
  const navigate = useNavigate();
  const [institutionName, setInstitutionName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [website, setWebsite] = useState("");
  const [institutionError, setInstitutionError] = useState("");
  const [institutionSuccess, setInstitutionSuccess] = useState(false);
  const [savingInstitution, setSavingInstitution] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  useEffect(() => {
    setInstitutionName(profile?.institution_name || "");
    setContactEmail(profile?.email || user?.email || "");
    setContactPhone(profile?.phone || "");
    setWebsite(profile?.website || "");
  }, [profile?.institution_name, profile?.email, profile?.phone, profile?.website, user?.email]);

  const handleSaveInstitution = async (e: React.FormEvent) => {
    e.preventDefault();
    setInstitutionError("");
    setInstitutionSuccess(false);
    setSavingInstitution(true);

    try {
      const { error: nameErr } = await saveInstitutionName(institutionName);
      if (nameErr) throw nameErr;

      if (user?.id) {
        const { error: updateErr } = await supabase
          .from("partners")
          .update({
            email: contactEmail.trim() || null,
            phone: contactPhone.trim() || null,
            website: website.trim() || null,
            updated_at: new Date().toISOString(),
          })
          .eq("id", user.id);
        if (updateErr) throw updateErr;
      }

      setInstitutionSuccess(true);
      await updateProfile();
      await refreshUserProfile();
      toast.success("Contact details and organization info saved!");
    } catch (err: any) {
      setInstitutionError(err.message || "Failed to update details");
      toast.error(err.message || "Failed to update details");
    } finally {
      setSavingInstitution(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordSuccess(false);

    if (!newPassword || !confirmPassword) {
      setPasswordError("Please fill in all fields");
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError("Password must be at least 6 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError("Passwords don't match");
      return;
    }

    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) {
      setPasswordError(error.message);
      return;
    }
    setPasswordSuccess(true);
    setNewPassword("");
    setConfirmPassword("");
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <PortalCard className="p-6">
        <h3 className="font-semibold mb-1">Integrations</h3>
        <p className="text-sm text-gray-500 mb-3">
          Google Calendar and Meet live on their own page.
        </p>
        <Link to="/partners/integrations">
          <Button variant="outline" className="rounded-xl">
            Open Integrations
          </Button>
        </Link>
      </PortalCard>

      <PortalCard className="p-6">
        <h3 className="font-semibold mb-4">Organization & Contact Details</h3>
        <form onSubmit={handleSaveInstitution} className="space-y-4">
          <div>
            <Label htmlFor="institution-name">Institution Name</Label>
            <Input
              id="institution-name"
              type="text"
              value={institutionName}
              onChange={(e) => setInstitutionName(e.target.value)}
              placeholder="Enter your institution name"
              className="mt-1 rounded-xl"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="contact-phone">Contact / Admissions Phone Number</Label>
              <Input
                id="contact-phone"
                type="tel"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                placeholder="+260 97 000 0000"
                className="mt-1 rounded-xl"
              />
            </div>
            <div>
              <Label htmlFor="contact-email">Admissions / Official Email</Label>
              <Input
                id="contact-email"
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                placeholder="admissions@institution.edu"
                className="mt-1 rounded-xl"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="contact-website">Website</Label>
            <Input
              id="contact-website"
              type="url"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder="https://institution.edu"
              className="mt-1 rounded-xl"
            />
          </div>
          <div className="space-y-4 text-sm pt-2">
            <div className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-800">
              <span className="text-gray-500">Account Login Email</span>
              <span className="font-medium">{user?.email || "Not set"}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-gray-500">Partner since</span>
              <span className="font-medium">
                {profile?.created_at
                  ? new Date(profile.created_at).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })
                  : "Not set"}
              </span>
            </div>
          </div>
          {institutionError && <p className="text-red-600 text-sm">{institutionError}</p>}
          {institutionSuccess && (
            <div className="flex items-center gap-2 text-emerald-600 text-sm">
              <CheckCircle2 className="w-4 h-4" />
              Organization & contact details saved successfully
            </div>
          )}
          <Button
            type="submit"
            disabled={savingInstitution}
            className="bg-skutopia-600 hover:bg-skutopia-700 text-white rounded-xl"
          >
            {savingInstitution ? "Saving…" : "Save Contact & Organization Info"}
          </Button>
        </form>
      </PortalCard>

      <PortalCard className="p-6">
        <h3 className="font-semibold mb-4">Change Password</h3>
        <form onSubmit={handleChangePassword} className="space-y-4">
          <div>
            <Label htmlFor="new-password">New Password</Label>
            <Input
              id="new-password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="mt-1 rounded-xl"
            />
          </div>
          <div>
            <Label htmlFor="confirm-password">Confirm Password</Label>
            <Input
              id="confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="mt-1 rounded-xl"
            />
          </div>
          {passwordError && <p className="text-red-600 text-sm">{passwordError}</p>}
          {passwordSuccess && (
            <div className="flex items-center gap-2 text-emerald-600 text-sm">
              <CheckCircle2 className="w-4 h-4" />
              Password updated successfully
            </div>
          )}
          <Button type="submit" className="bg-skutopia-600 hover:bg-skutopia-700 text-white rounded-xl">
            Update Password
          </Button>
        </form>
      </PortalCard>

      <PortalCard className="p-6 border-red-100">
        <h3 className="font-semibold mb-1 text-red-700">Delete account</h3>
        <p className="text-sm text-gray-500 mb-4">
          Permanently delete your partner account, programs, applications, and messages. This
          cannot be undone.
        </p>
        <div className="space-y-3">
          <div>
            <Label htmlFor="delete-confirm">
              Type <span className="font-semibold">DELETE</span> to confirm
            </Label>
            <Input
              id="delete-confirm"
              type="text"
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              className="mt-1 rounded-xl"
              placeholder="DELETE"
            />
          </div>
          {deleteError && <p className="text-red-600 text-sm">{deleteError}</p>}
          <Button
            type="button"
            variant="destructive"
            disabled={deletingAccount || deleteConfirm.trim() !== "DELETE"}
            className="rounded-xl"
            onClick={async () => {
              if (deleteConfirm.trim() !== "DELETE") return;
              if (
                !window.confirm("Are you sure you want to permanently delete your partner account?")
              ) {
                return;
              }
              setDeletingAccount(true);
              setDeleteError("");
              try {
                const { data, error } = await supabase.functions.invoke("delete-partner-account", {
                  body: {},
                });
                if (error) throw error;
                if (data?.error) throw new Error(String(data.error));
                toast.success("Account deleted");
                await logout();
                navigate("/");
              } catch (e: unknown) {
                setDeleteError(e instanceof Error ? e.message : "Could not delete account.");
              } finally {
                setDeletingAccount(false);
              }
            }}
          >
            {deletingAccount ? "Deleting…" : "Delete partner account"}
          </Button>
        </div>
      </PortalCard>
    </div>
  );
}
